import { styles } from "@/reports/reportStyles";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import useApproachData from "@/composables/approachData";
import { sortStructure } from "@/util/sortStructure";

function setApproachDocDefinition(structure) {
  const configStore = useConfigStore();
  const { bridgeRailingDesc, transitionDesc } = useApproachData();
  const signsTable = [];
  signsTable.push(getSignsTableHeader());
  sortStructure(structure, "structureSignings", "signType");
  getSignsOpeningTable(signsTable, structure);

  let docDefinition = {
    header: function () {
      return getReportHeader("Approach Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Approach Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Approach Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "32%",
            text: "B.AP.01 Approach Roadway Alignment: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.BRIDGE_CONDITION_RATING,
              structure?.inspection?.apprAlign
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Alignment Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.APPROACH_ALIGNMENT
        )?.notes,
      },
      {
        columns: [
          {
            width: "22%",
            text: "AP.01 Approach Roadway: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.apprRoadCondRate
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "18%",
            text: "AP.02 Approach Slab: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.apprSlabCondRate
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },

      {
        columns: [
          {
            width: "22%",
            text: "B.C.05 Railing: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.railRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "18%",
            text: "B.C.06 Transition: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.transRating),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Approach Details and Inspection Notes",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "28%",
            text: "AP.03 Approach Pavement Width: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.apprPavementWidth + " ft",
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Pavement: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.APPROACH_PAVEMENT
        )?.notes,
      },
      {
        text: "Drainage: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.APPROACH_DRAINAGE
        )?.notes,
      },
      {
        text: "Shoulders: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.APPROACH_SHOULDERS
        )?.notes,
      },
      {
        text: "Approach Slab: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.APPROACH_SLAB
        )?.notes,
      },
      {
        columns: [
          {
            width: "32%",
            text: "AP.04 Pavement Relief Joints Present? ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.reliefJoint,
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.PAVEMENT_RELIEF_JOINTS
        )?.notes,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Railings: ",
                bold: true,
                decoration: "underline",
              },
            ],
            [
              {
                text: [
                  {
                    text: "B.C.05 Condition: ",
                    bold: true,
                  },
                  configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure.inspection.railRating),
                ],
              },
            ],
            [
              {
                text: [
                  {
                    text: "B.RH.01 Crash Level: ",
                    bold: true,
                  },
                  structure.structureBridge.bridgeRailings,
                ],
              },
            ],
            [
              {
                width: "auto",
                text: [{ text: "Description: ", bold: true }, bridgeRailingDesc.value],
                margin: [0, 0, 0, 0],
              },
            ],
            [
              {
                text: "Notes: ",
                bold: true,
                margin: [0, 0, 0, 0],
              },
            ],
            [
              {
                text: structure?.inspectionComments?.find(
                  (element) => element?.commentType == INSPECTION_COMMENTS.BRIDGE_RAILING
                )?.notes,
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Transitions: ",
                bold: true,
                decoration: "underline",
              },
            ],
            [
              {
                text: [
                  {
                    text: "B.C.06 Condition: ",
                    bold: true,
                  },
                  configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure.inspection.transRating),
                ],
              },
            ],
            [
              {
                text: [
                  {
                    text: "B.RH.02 Crash Level: ",
                    bold: true,
                  },
                  structure.structureBridge.transitions,
                ],
              },
            ],
            [
              {
                width: "auto",
                text: [{ text: "Description: ", bold: true }, transitionDesc.value],
                margin: [0, 0, 0, 0],
              },
            ],
            [
              {
                text: "Notes: ",
                bold: true,
                margin: [0, 0, 0, 0],
              },
            ],
            [
              {
                text: structure?.inspectionComments?.find(
                  (element) => element?.commentType == INSPECTION_COMMENTS.TRANSITION
                )?.notes,
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        stack: [
          {
            table: {
              headerRows: 0,
              widths: ["100%"],
              body: [
                [
                  {
                    text: "Signs",
                    fillColor: "#ebebeb",
                    alignment: "center",
                    style: ["sectionHeader"],
                  },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 5, 0, 5],
          },
          {
            table: {
              headerRows: 1,
              widths: ["25%", "15%", "9%", "9%", "9%", "9%", "*"],
              body: signsTable,
            },
          },
        ],
      },
    ],
    unbreakable: true,
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}

function getSignsTableHeader() {
  return [
    {
      text: "AP.05\n Type of Sign",
      style: ["tableHeader"],
    },
    {
      text: "AP.06\n Sign Message",
      style: ["tableHeader"],
    },
    {
      text: "AP.07\n Near\n Advance",
      style: ["tableHeader"],
    },
    {
      text: "AP.08\n Far\n Advance",
      style: ["tableHeader"],
    },
    {
      text: "AP.09\n Bridge Site\n Near",
      style: ["tableHeader"],
    },
    {
      text: "AP.10\n Bridge\n Site Far",
      style: ["tableHeader"],
    },

    {
      text: "AP.11\n Signing Notes",
      style: ["tableHeader"],
    },
  ];
}

function getSignsOpeningTable(signingDetails, structure) {
  const configStore = useConfigStore();

  for (const openingDetail of structure?.structureSignings || []) {
    signingDetails.push(
      [
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.SIGN_TYPE, openingDetail?.signType),
          style: ["tableBody"],
        },
        {
          text: openingDetail?.signMessage,
          style: ["tableBody"],
        },
        {
          text: openingDetail?.nearAdvanceCond,
          style: ["tableBody"],
        },
        {
          text: openingDetail?.farAdvanceCond,
          style: ["tableBody"],
        },
        {
          text: openingDetail?.nearSiteCond,
          style: ["tableBody"],
        },
        {
          text: openingDetail?.farSiteCond,
          style: ["tableBody"],
        },
        {
          text: openingDetail?.notes,
          style: ["tableBody"],
        },
      ].map((d) => {
        return {
          text: d,
          alignment: "center",
        };
      })
    );
  }
}

export { setApproachDocDefinition };
